/** Extract alias from a Qixit email identity (e.g. beta01@qixit.com → beta01). */
export function extractAlias(address?: string | null): string {
  if (!address) return '';
  const trimmed = address.trim();
  if (!trimmed) return '';
  const at = trimmed.indexOf('@');
  return (at >= 0 ? trimmed.slice(0, at) : trimmed).trim();
}

/** Normalize to lowercase alias@qixit.com */
export function normalizeQixitAddress(addressOrAlias: string, domain = 'qixit.com'): string | null {
  const trimmed = (addressOrAlias || '').trim();
  if (!trimmed) return null;
  const alias = extractAlias(trimmed.includes('@') ? trimmed : `${trimmed}@${domain}`);
  if (!alias) return null;
  return `${alias.toLowerCase()}@${domain.toLowerCase()}`;
}

export function cacheKeyFromAddress(address?: string | null): string {
  return normalizeQixitAddress(address || '') ?? '';
}

/** Inbox/sent list rows — nickname (alias) or alias alone. */
export function formatListSenderLabel(fromAddress?: string | null, nickname?: string | null): string {
  const alias = extractAlias(fromAddress);
  const nick = (nickname || '').trim();
  if (nick && alias) return `${nick} (${alias})`;
  return alias || extractAlias(fromAddress) || 'Unknown';
}

/** Inbox list + message viewer From: line — display name and qixit alias. */
export function formatInboxFromLabel(fromName?: string | null, fromAddress?: string | null): string {
  const alias = extractAlias(fromAddress);
  const display = (fromName || '').trim();
  if (display && alias && display.toLowerCase() !== alias.toLowerCase()) {
    return `${display} (${alias})`;
  }
  return alias || display || 'Unknown';
}

/** Compose recipient picker — alias only (no @domain). */
export function formatComposeRecipientAlias(address?: string | null, username?: string | null): string {
  return extractAlias(address) || (username || '').trim() || 'Unknown';
}

/**
 * When tbContacts.DisplayName is still the alias, prefer the profile lookup full name
 * (same rule as Address Book listing).
 */
export function resolveStoredContactDisplayName(
  contact: { displayName?: string | null; address?: string | null },
  lookupFullName?: string | null
): string {
  const contactUsername = formatComposeRecipientAlias(contact.address);
  const canonicalDisplayName = (lookupFullName || '').trim();
  const storedDisplayName = (contact.displayName || '').trim();
  const shouldUseCanonicalDisplay =
    canonicalDisplayName.length > 0 &&
    storedDisplayName.length > 0 &&
    storedDisplayName.toLowerCase() === contactUsername.toLowerCase() &&
    canonicalDisplayName.toLowerCase() !== storedDisplayName.toLowerCase();

  return shouldUseCanonicalDisplay ? canonicalDisplayName : (storedDisplayName || contactUsername);
}

/** Compose recipient picker — top line: nickname, else display/full name when distinct from alias. */
export function formatComposeRecipientPrimary(options: {
  nickname?: string | null;
  displayName?: string | null;
  fullName?: string | null;
  canonicalFullName?: string | null;
  address?: string | null;
  username?: string | null;
}): string {
  const nick = (options.nickname || '').trim();
  if (nick) return nick;

  const alias = formatComposeRecipientAlias(options.address, options.username);
  const resolvedDisplay = options.canonicalFullName !== undefined
    ? resolveStoredContactDisplayName(
        { displayName: options.displayName, address: options.address },
        options.canonicalFullName
      )
    : (options.displayName || options.fullName || '').trim();

  if (resolvedDisplay && alias && resolvedDisplay.toLowerCase() !== alias.toLowerCase()) {
    return resolvedDisplay;
  }

  const fullName = (options.fullName || options.canonicalFullName || '').trim();
  if (fullName && alias && fullName.toLowerCase() !== alias.toLowerCase()) {
    return fullName;
  }

  return alias || resolvedDisplay || 'Unknown';
}

/** Selected value label in compose autocomplete input. */
export function formatComposeRecipientOptionLabel(options: {
  nickname?: string | null;
  displayName?: string | null;
  fullName?: string | null;
  canonicalFullName?: string | null;
  address?: string | null;
  username?: string | null;
}): string {
  const primary = formatComposeRecipientPrimary(options);
  const alias = formatComposeRecipientAlias(options.address, options.username);
  if (primary.toLowerCase() === alias.toLowerCase()) {
    return alias;
  }
  return `${primary} (${alias})`;
}
